// Tiny helper module for the signup SPA's Cloudflare Turnstile gate.
//
// The site key is fetched at runtime from GET /api/public/signup/config,
// which reads it out of Vault beside the siteverify secret it pairs with
// (see apps/api/src/routes/public-signup-config.ts). It used to be a
// build-time constant (VITE_TURNSTILE_SITEKEY) baked into the bundle;
// that split the pair across two provisioning systems and let them
// drift — seeding only the secret took public signup down, because the
// API started failing closed while the deployed bundle had no key to
// render a widget with.
//
// Consequence for callers: the key is not knowable at module load, so
// these helpers take it as an argument instead of reading a global.
// App.tsx holds it in boot state and passes it down; nothing here keeps
// mutable module state, which also keeps the helpers trivially testable.
//
// Both halves are optional. No site key means the SPA renders no widget
// and POSTs a null token, which the API accepts only while the secret is
// likewise unset (dev / pre-rollout). A documented fallback for local
// dev is Cloudflare's published test site key
// `1x00000000000000000000AA`, which always issues a valid token — set
// turnstile_site_key to it in the dev Vault doc to exercise the wired
// path without provisioning a real key.

export const TURNSTILE_TEST_SITEKEY = '1x00000000000000000000AA';

/** Normalizes a fetched site key: trims, and treats blank as absent. */
export function normalizeSitekey(raw: string | null | undefined): string {
  return (raw ?? '').trim();
}

/** True when a usable site key is present, i.e. the widget should render. */
export function isTurnstileEnabled(sitekey: string | null | undefined): boolean {
  return normalizeSitekey(sitekey).length > 0;
}

// Minimal ambient shape for the global injected by
// https://challenges.cloudflare.com/turnstile/v0/api.js. We only call
// `render` and `remove`; everything else is opaque.
export interface TurnstileGlobal {
  render(
    element: HTMLElement,
    options: {
      sitekey: string;
      callback?: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
      theme?: 'light' | 'dark' | 'auto';
      size?: 'normal' | 'compact' | 'flexible';
    },
  ): string;
  remove(widgetId: string): void;
  reset(widgetId?: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal;
  }
}
